import { isRedirect } from '@gatsbyjs/reach-router';
import React, { PropsWithChildren } from 'react';

interface Props {
	fallback: React.ElementType<Omit<Props, 'fallback'> & { error?: Error }>;
	onError?: (error: Error) => void;
}

class ErrorBoundary extends React.Component<
	PropsWithChildren<Props>,
	{ error?: Error; hasError: boolean }
> {
	// eslint-disable-next-line react/static-property-placement
	static defaultProps: { onError: (error: Error) => void };

	constructor(props: PropsWithChildren<Props>) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(error: Error): { error?: Error; hasError?: boolean } {
		return isRedirect(error) ? {} : { error, hasError: true };
	}

	componentDidCatch(error: Error): void {
		if (isRedirect(error)) throw error; // @see https://reach.tech/router/api/isRedirect
		this.props.onError && this.props.onError(error);
	}

	render(): React.ReactNode | null {
		const { fallback: Fallback, ...restProps } = this.props;
		const { hasError, error } = this.state;

		if (hasError) {
			return <Fallback error={error} {...restProps} />;
		}

		return restProps.children;
	}
}

ErrorBoundary.defaultProps = {
	onError: (error: Error) => {
		// eslint-disable-next-line no-console
		console.error('Error boundary error:', error);
	},
};

export default ErrorBoundary;
